<template>
  <div class="day-metric-bar">
    <TeleportTooltip v-if="mode === 'prediction'" block>
      <template #trigger>
        <div class="relative w-full h-7 bg-gray-200 rounded-full">
          <div
            v-if="barWidth >= 5"
            class="absolute inset-y-0 left-0 rounded-full"
            :class="barColor"
            :style="{ width: barWidth + '%' }"
          ></div>
          <div class="absolute inset-0 flex items-center justify-center z-10">
            <span class="label-pill inline-flex items-center gap-0.5 whitespace-nowrap" :class="pillClass">
              <span>{{ label }}</span>
              <svg
                v-if="showWarningIcon"
                class="warning-icon shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </span>
          </div>
        </div>
      </template>
      <p>Predicted probability: <strong>{{ rawProba }}</strong></p>
      <div v-if="warningReasons.length" class="mt-2 pt-2 border-t border-gray-600">
        <p class="flex items-start gap-1.5 font-semibold">
          <svg
            class="warning-icon mt-0.5 shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <span>Warning reason{{ warningReasons.length > 1 ? 's' : '' }}:</span>
        </p>
        <ul class="mt-1 ml-5 list-disc space-y-0.5">
          <li v-for="(reason, idx) in warningReasons" :key="idx">{{ reason }}</li>
        </ul>
      </div>
    </TeleportTooltip>
    <div
      v-else
      class="relative w-full h-7 bg-gray-200 rounded-full"
    >
      <div
        v-if="barWidth >= 5"
        class="absolute inset-y-0 left-0 rounded-full"
        :class="barColor"
        :style="{ width: barWidth + '%' }"
      ></div>
      <div class="absolute inset-0 flex items-center justify-center z-10">
        <span class="label-pill inline-flex items-center gap-0.5 whitespace-nowrap" :class="pillClass">
          <span>{{ label }}</span>
          <svg
            v-if="showWarningIcon"
            class="warning-icon shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import TeleportTooltip from './TeleportTooltip.vue'

export default {
  name: 'DayMetricBar',
  components: { TeleportTooltip },
  props: {
    mode: {
      type: String,
      default: 'duration',
      validator: (value) => ['duration', 'prediction'].includes(value)
    },
    /** Yes / No / Unsure — used when mode is 'prediction' */
    verdict: {
      type: String,
      default: ''
    },
    durationHours: {
      type: Number,
      default: 0
    },
    /** Fraction of day (0–1), e.g. device_turned_on_percent_of_day */
    percent: {
      type: Number,
      default: 0
    },
    barColor: {
      type: String,
      default: 'bg-blue-600'
    },
    textClass: {
      type: String,
      default: 'text-gray-700'
    },
    decimals: {
      type: Number,
      default: 1
    },
    /** Reasons shown in prediction tooltip when ! is present */
    warningReasons: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    percentDisplay() {
      return Math.min(Number(this.percent || 0) * 100, 100)
    },
    barWidth() {
      return this.percentDisplay
    },
    showWarningIcon() {
      return this.isWarningText
    },
    isWarningText() {
      return (
        this.textClass.includes('red')
        || this.textClass.includes('maroon')
        || this.textClass.includes('800020')
        || (this.mode === 'prediction' && this.warningReasons.length > 0)
      )
    },
    pillClass() {
      if (this.isWarningText) {
        const colorClass = (
          this.textClass.includes('red')
          || this.textClass.includes('maroon')
          || this.textClass.includes('800020')
        ) ? this.textClass : 'text-[#800020]'
        return `pill-warning ${colorClass}`
      }
      return 'pill-default'
    },
    rawProba() {
      const p = Number(this.percent || 0)
      return isNaN(p) ? '0.000' : p.toFixed(3)
    },
    label() {
      if (this.mode === 'prediction') {
        return this.verdict || 'Unsure'
      }
      const hours = Number(this.durationHours || 0)
      const hoursStr = isNaN(hours) ? '0' : hours.toFixed(this.decimals)
      const pctStr = isNaN(this.percentDisplay) ? '0' : Math.round(this.percentDisplay)
      return `${hoursStr}h (${pctStr}%)`
    }
  }
}
</script>

<style scoped>
.day-metric-bar {
  width: 10rem;
  margin-left: auto;
  margin-right: auto;
  flex-shrink: 0;
}

.label-pill {
  font-size: 0.675rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 0.05rem 0.35rem 0.05rem 0.3rem;
  border-radius: 0.15rem;
  line-height: 1.1;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.pill-default {
  color: #1f2937;
  background: rgba(255, 255, 255, 0.75);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
}

.pill-filled {
  color: #fff;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}

.pill-muted {
  color: #374151;
  background: rgba(255, 255, 255, 0.75);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
}

.pill-warning {
  font-weight: 700;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
}

.warning-icon {
  width: 0.75rem;
  height: 0.75rem;
  color: currentColor;
}
</style>
